"use client"

import { useEffect, useRef, useState } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Modal,
  Button,
  Form,
  InputGroup,
  Table,
  Badge,
  Alert,
} from "react-bootstrap"
import { toast } from '../common/Toast/toast';
import { getBatches } from "../../../services/BatchService"
import { getTrainers } from "../../../services/TrainerService"
import { getStudents } from "../../../services/StudentService"
import { getClassrooms } from "../../../services/ClassroomService.js"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../../context/AuthContext"

const AdminStatistics = () => {
  const [loading, setLoading] = useState(true)
  const [totalStats, setTotalStats] = useState({
    totalTrainers: 0,
    totalBatches: 0,
    totalStudents: 0,
    totalClassrooms: 0,
  })

  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState("")
  const [modalData, setModalData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedItem, setSelectedItem] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)

  // All data storage
  const [allData, setAllData] = useState({
    trainers: [],
    batches: [],
    students: [],
    classrooms: [],
  })

  const fetchAllData = async () => {
    try {
      setLoading(true)

      // Fetch all data in parallel
      const [batchesResponse, trainersResponse, studentsResponse, classroomsResponse] = await Promise.all([
        getBatches(500000, 1, ""),
        getTrainers(500000, 1, ""),
        getStudents(500000, 1, ""),
        getClassrooms(500000, 1, ""),
      ])

      const batches = batchesResponse.data
      const trainers = trainersResponse.data
      const students = studentsResponse.data
      const classrooms = classroomsResponse.data

      // Store all data
      setAllData({
        trainers,
        batches,
        students,
        classrooms,
      })

      // Calculate total stats
      const totalStudents = batches.reduce((total, batch) => {
        const ids = batch?.userId
          ? Array.isArray(batch.userId)
            ? batch.userId
                .map((u) => (u && typeof u === "object" ? u.id || u._id : u))
                .map((x) => String(x || "").trim())
                .filter(Boolean)
            : String(batch.userId)
                .split(",")
                .map((x) => x.trim())
                .filter(Boolean)
          : []
        return total + ids.length
      }, 0)

      setTotalStats({
        totalTrainers: trainers.length,
        totalBatches: batches.length,
        totalStudents: totalStudents,
        totalClassrooms: classrooms.length,
      })
    } catch (err) {
      console.error("Error fetching data:", err)
      toast.error("Failed to fetch dashboard data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAllData()
  }, [])

  // Handle modal opening
  const handleCardClick = (type) => {
    setModalType(type)
    setSearchTerm("")
    setSelectedItem(null)
    setModalLoading(true)
    setShowModal(true)

    // Set modal data based on type
    let data = []
    switch (type) {
      case "trainers":
        data = allData.trainers.map((trainer) => ({
          ...trainer,
          batchCount: allData.batches.filter(
            (batch) => Array.isArray(batch.trainerId) && batch.trainerId.some((t) => t.id === trainer.id),
          ).length,
        }))
        break
      case "batches":
        data = allData.batches.map((batch) => ({
          ...batch,
          courseName: Array.isArray(batch.courseId) && batch.courseId.length > 0 ? batch.courseId[0].name : "N/A",
          trainerName:
            Array.isArray(batch.trainerId) && batch.trainerId.length > 0 ? batch.trainerId[0].trainerId : "N/A",
          classroomNo:
            Array.isArray(batch.classroom) && batch.classroom.length > 0 ? batch.classroom[0].classroomNo : "N/A",
          studentCount: Array.isArray(batch.userId) ? batch.userId.length : 0,
          formattedStartTime: new Date(batch.startTime).toLocaleDateString(),
          formattedEndTime: new Date(batch.endTime).toLocaleDateString(),
        }))
        break
      case "students":
        data = allData.students.map((student) => ({
          ...student,
          batchCount: allData.batches.filter(
            (batch) => {
              const ids = batch?.userId
                ? Array.isArray(batch.userId)
                  ? batch.userId
                      .map((u) => (u && typeof u === "object" ? u.id || u._id : u))
                      .map((x) => String(x || "").trim())
                      .filter(Boolean)
                  : String(batch.userId)
                      .split(",")
                      .map((x) => x.trim())
                      .filter(Boolean)
                : []
              const sid = student.id || student._id
              return ids.includes(String(sid))
            },
          ).length,
        }))
        break
      case "classrooms":
        data = allData.classrooms.map((classroom) => ({
          ...classroom,
          batchCount: allData.batches.filter(
            (batch) => Array.isArray(batch.classroom) && batch.classroom.some((c) => c.id === classroom.id),
          ).length,
          formattedDate: new Date(classroom.date).toLocaleDateString(),
          formattedStartTime: new Date(classroom.startTime).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          formattedEndTime: new Date(classroom.endTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        }))
        break
      default:
        data = []
    }

    setModalData(data)
    setFilteredData(data)
    setModalLoading(false)
  }

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term)
    if (!term) {
      setFilteredData(modalData)
      return
    }

    const filtered = modalData.filter((item) => {
      switch (modalType) {
        case "trainers":
          return (
            item.trainerId?.toLowerCase().includes(term.toLowerCase()) ||
            item.specialization?.toLowerCase().includes(term.toLowerCase()) ||
            item.email?.toLowerCase().includes(term.toLowerCase())
          )
        case "batches":
          return (
            item.name?.toLowerCase().includes(term.toLowerCase()) ||
            item.courseName?.toLowerCase().includes(term.toLowerCase()) ||
            item.trainerName?.toLowerCase().includes(term.toLowerCase())
          )
        case "students":
          return (
            item.firstName?.toLowerCase().includes(term.toLowerCase()) ||
            item.lastName?.toLowerCase().includes(term.toLowerCase()) ||
            item.email?.toLowerCase().includes(term.toLowerCase()) ||
            item.userId?.toLowerCase().includes(term.toLowerCase())
          )
        case "classrooms":
          return (
            item.classroomNo?.toLowerCase().includes(term.toLowerCase()) ||
            item.slug?.toLowerCase().includes(term.toLowerCase()) ||
            item.status?.toLowerCase().includes(term.toLowerCase())
          )
        default:
          return false
      }
    })
    setFilteredData(filtered)
  }

  // Handle item selection for details
  const handleItemClick = (item) => {
    if (modalType === "trainers") {
      // Get trainer's batches
      const trainerBatches = allData.batches.filter(
        (batch) => Array.isArray(batch.trainerId) && batch.trainerId.some((t) => t.id === item.id),
      )
      setSelectedItem({ ...item, batches: trainerBatches })
    } else {
      setSelectedItem(item)
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setModalType("")
    setModalData([])
    setFilteredData([])
    setSearchTerm("")
    setSelectedItem(null)
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      Available: "success",
      Occupied: "danger",
      Maintenance: "warning",
      Reserved: "info",
      Online: "success",
      Offline: "warning",
      Hybrid: "info",
    }
    return (
      <Badge bg={statusConfig[status] || "secondary"} className="px-2 py-1">
        {status}
      </Badge>
    )
  }
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();
  useEffect(() => {
    if (!hasPermission('student-id-card', 'view','lms')) {
      if (!hasShownToast.current) {
        toast.error('You do not have permission to add departments.');
        navigate('/');
        // console.log("hello")
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);
  if (loading) {
    return (
      <Container fluid>
        <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
          <div className="spinner-border text-warning" role="status" style={{ width: "3rem", height: "3rem" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    )
  }

  return (
    <Container fluid>      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Card>
            <CardBody className="bg-warning text-white text-center">
              <h2 className="mb-0 text-white ">
                <i className="fa fa-tachometer-alt me-2"></i>
                Admin Statistics
              </h2>
              <p className="mb-0 mt-2">Click on any statistic card to view detailed information</p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card
            className="text-center border-primary shadow-sm h-100 cursor-pointer"
            style={{ cursor: "pointer", transition: "transform 0.2s" }}
            onClick={() => handleCardClick("trainers")}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <CardBody>
              <div className="text-primary mb-2">
                <i className="fa fa-user-tie" style={{ fontSize: "2rem" }}></i>
              </div>
              <h3 className="text-primary">{totalStats.totalTrainers}</h3>
              <p className="mb-0 fw-bold">Total Trainers</p>
              <small className="text-muted">Click to view all trainers</small>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card
            className="text-center border-success shadow-sm h-100"
            style={{ cursor: "pointer", transition: "transform 0.2s" }}
            onClick={() => handleCardClick("batches")}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <CardBody>
              <div className="text-success mb-2">
                <i className="fa fa-layer-group" style={{ fontSize: "2rem" }}></i>
              </div>
              <h3 className="text-success">{totalStats.totalBatches}</h3>
              <p className="mb-0 fw-bold">Total Batches</p>
              <small className="text-muted">Click to view all batches</small>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card
            className="text-center border-info shadow-sm h-100"
            style={{ cursor: "pointer", transition: "transform 0.2s" }}
            onClick={() => handleCardClick("students")}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <CardBody>
              <div className="text-info mb-2">
                <i className="fa fa-graduation-cap" style={{ fontSize: "2rem" }}></i>
              </div>
              <h3 className="text-info">{totalStats.totalStudents}</h3>
              <p className="mb-0 fw-bold">Total Students</p>
              <small className="text-muted">Click to view all students</small>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card
            className="text-center border-warning shadow-sm h-100"
            style={{ cursor: "pointer", transition: "transform 0.2s" }}
            onClick={() => handleCardClick("classrooms")}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <CardBody>
              <div className="text-warning mb-2">
                <i className="fa fa-door-open" style={{ fontSize: "2rem" }}></i>
              </div>
              <h3 className="text-warning">{totalStats.totalClassrooms}</h3>
              <p className="mb-0 fw-bold">Total Classrooms</p>
              <small className="text-muted">Click to view all classrooms</small>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Modal */}
      <Modal show={showModal} onHide={closeModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i
              className={`fa fa-${modalType === "trainers" ? "user-tie" : modalType === "batches" ? "layer-group" : modalType === "students" ? "graduation-cap" : "door-open"} me-2`}
            ></i>
            {modalType.charAt(0).toUpperCase() + modalType.slice(1)} Management
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {modalLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : selectedItem ? (
            // Detail View
            <div>
              <Button variant="outline-secondary" size="sm" onClick={() => setSelectedItem(null)} className="mb-3">
                <i className="fa fa-arrow-left me-2"></i>Back to List
              </Button>

              {modalType === "trainers" && (
                <div>
                  <Card className="mb-3">
                    <CardBody>
                      <h5 className="text-primary">Trainer Details</h5>
                      <Row>
                        <Col md={6}>
                          <p>
                            <strong>Trainer ID:</strong> {selectedItem.trainerId}
                          </p>
                          <p>
                            <strong>Specialization:</strong> {selectedItem.specialization}
                          </p>
                          <p>
                            <strong>Email:</strong> {selectedItem.email}
                          </p>
                        </Col>
                        <Col md={6}>
                          <p>
                            <strong>Phone:</strong> {selectedItem.phone || "N/A"}
                          </p>
                          <p>
                            <strong>Total Batches:</strong> {selectedItem.batches?.length || 0}
                          </p>
                          <p>
                            <strong>Status:</strong> {getStatusBadge("Active")}
                          </p>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardBody>
                      <h6 className="text-success">Assigned Batches</h6>
                      {selectedItem.batches?.length > 0 ? (
                        <Table striped bordered hover size="sm">
                          <thead>
                            <tr>
                              <th>Batch Name</th>
                              <th>Course</th>
                              <th>Classroom</th>
                              <th>Students</th>
                              <th>Start Date</th>
                              <th>Mode</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedItem.batches.map((batch) => (
                              <tr key={batch.id}>
                                <td>{batch.name}</td>
                                <td>
                                  {Array.isArray(batch.courseId) && batch.courseId.length > 0
                                    ? batch.courseId[0].name
                                    : "N/A"}
                                </td>
                                <td>
                                  {Array.isArray(batch.classroom) && batch.classroom.length > 0
                                    ? batch.classroom[0].classroomNo
                                    : "N/A"}
                                </td>
                                <td>{Array.isArray(batch.userId) ? batch.userId.length : 0}</td>
                                <td>{new Date(batch.startTime).toLocaleDateString()}</td>
                                <td>{getStatusBadge(batch.mode)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <Alert variant="info">No batches assigned to this trainer.</Alert>
                      )}
                    </CardBody>
                  </Card>
                </div>
              )}

              {modalType === "batches" && (
                <Card>
                  <CardBody>
                    <h5 className="text-success">Batch Details</h5>
                    <Row>
                      <Col md={6}>
                        <p>
                          <strong>Batch Name:</strong> {selectedItem.name}
                        </p>
                        <p>
                          <strong>Course:</strong> {selectedItem.courseName}
                        </p>
                        <p>
                          <strong>Trainer:</strong> {selectedItem.trainerName}
                        </p>
                        <p>
                          <strong>Classroom:</strong> {selectedItem.classroomNo}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p>
                          <strong>Students:</strong> {selectedItem.studentCount}
                        </p>
                        <p>
                          <strong>Start Date:</strong> {selectedItem.formattedStartTime}
                        </p>
                        <p>
                          <strong>End Date:</strong> {selectedItem.formattedEndTime}
                        </p>
                        <p>
                          <strong>Mode:</strong> {getStatusBadge(selectedItem.mode)}
                        </p>
                      </Col>
                    </Row>
                    {selectedItem.mode !== "Offline" && (
                      <div className="mt-3">
                        <h6>Meeting Information</h6>
                        <p>
                          <strong>Meeting ID:</strong> {selectedItem.meetingId || "N/A"}
                        </p>
                        <p>
                          <strong>Meeting Link:</strong>{" "}
                          {selectedItem.meetingLink ? (
                            <a href={selectedItem.meetingLink} target="_blank" rel="noopener noreferrer">
                              Join Meeting
                            </a>
                          ) : (
                            "N/A"
                          )}
                        </p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}

              {modalType === "students" && (
                <Card>
                  <CardBody>
                    <h5 className="text-info">Student Details</h5>
                    <Row>
                      <Col md={6}>
                        <p>
                          <strong>Name:</strong> {selectedItem.firstName} {selectedItem.lastName}
                        </p>
                        <p>
                          <strong>User ID:</strong> {selectedItem.userId}
                        </p>
                        <p>
                          <strong>Email:</strong> {selectedItem.email}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p>
                          <strong>Phone:</strong> {selectedItem.phone || "N/A"}
                        </p>
                        <p>
                          <strong>Enrolled Batches:</strong> {selectedItem.batchCount}
                        </p>
                        <p>
                          <strong>Status:</strong> {getStatusBadge("Active")}
                        </p>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}

              {modalType === "classrooms" && (
                <Card>
                  <CardBody>
                    <h5 className="text-warning">Classroom Details</h5>
                    <Row>
                      <Col md={6}>
                        <p>
                          <strong>Classroom No:</strong> {selectedItem.classroomNo}
                        </p>
                        <p>
                          <strong>Slug:</strong> {selectedItem.slug}
                        </p>
                        <p>
                          <strong>Status:</strong> {getStatusBadge(selectedItem.status)}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p>
                          <strong>Date:</strong> {selectedItem.formattedDate}
                        </p>
                        <p>
                          <strong>Start Time:</strong> {selectedItem.formattedStartTime}
                        </p>
                        <p>
                          <strong>End Time:</strong> {selectedItem.formattedEndTime}
                        </p>
                      </Col>
                    </Row>
                    <div className="mt-3">
                      <h6>Description</h6>
                      <p>{selectedItem.description || "No description available"}</p>
                    </div>
                    <div className="mt-3">
                      <p>
                        <strong>Used in Batches:</strong> {selectedItem.batchCount}
                      </p>
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>
          ) : (
            // List View
            <div>
              {/* Search */}
              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <i className="fa fa-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={`Search ${modalType}...`}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </InputGroup>

              {/* Results */}
              {filteredData.length === 0 ? (
                <Alert variant="info" className="text-center">
                  No {modalType} found {searchTerm && `matching "${searchTerm}"`}
                </Alert>
              ) : (
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      {modalType === "trainers" && (
                        <>
                          <th>Trainer ID</th>
                          <th>Specialization</th>
                          <th>Email</th>
                          <th>Batches</th>
                          <th>Action</th>
                        </>
                      )}
                      {modalType === "batches" && (
                        <>
                          <th>Batch Name</th>
                          <th>Course</th>
                          <th>Trainer</th>
                          <th>Classroom</th>
                          <th>Students</th>
                          <th>Mode</th>
                          <th>Action</th>
                        </>
                      )}
                      {modalType === "students" && (
                        <>
                          <th>Name</th>
                          <th>User ID</th>
                          <th>Email</th>
                          <th>Batches</th>
                          <th>Action</th>
                        </>
                      )}
                      {modalType === "classrooms" && (
                        <>
                          <th>Classroom No</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Time</th>
                          <th>Batches</th>
                          <th>Action</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((item) => (
                      <tr key={item.id}>
                        {modalType === "trainers" && (
                          <>
                            <td>{item.trainerId}</td>
                            <td>{item.specialization}</td>
                            <td>{item.email}</td>
                            <td>
                              <Badge bg="primary">{item.batchCount}</Badge>
                            </td>
                            <td>
                              <Button variant="outline-primary" size="sm" onClick={() => handleItemClick(item)}>
                                View Details
                              </Button>
                            </td>
                          </>
                        )}
                        {modalType === "batches" && (
                          <>
                            <td>{item.name}</td>
                            <td>{item.courseName}</td>
                            <td>{item.trainerName}</td>
                            <td>{item.classroomNo}</td>
                            <td>
                              <Badge bg="success">{item.studentCount}</Badge>
                            </td>
                            <td>{getStatusBadge(item.mode)}</td>
                            <td>
                              <Button variant="outline-success" size="sm" onClick={() => handleItemClick(item)}>
                                View Details
                              </Button>
                            </td>
                          </>
                        )}
                        {modalType === "students" && (
                          <>
                            <td>
                              {item.firstName} {item.lastName}
                            </td>
                            <td>{item.userId}</td>
                            <td>{item.email}</td>
                            <td>
                              <Badge bg="info">{item.batchCount}</Badge>
                            </td>
                            <td>
                              <Button variant="outline-info" size="sm" onClick={() => handleItemClick(item)}>
                                View Details
                              </Button>
                            </td>
                          </>
                        )}
                        {modalType === "classrooms" && (
                          <>
                            <td>{item.classroomNo}</td>
                            <td>{getStatusBadge(item.status)}</td>
                            <td>{item.formattedDate}</td>
                            <td>
                              {item.formattedStartTime} - {item.formattedEndTime}
                            </td>
                            <td>
                              <Badge bg="warning">{item.batchCount}</Badge>
                            </td>
                            <td>
                              <Button variant="outline-warning" size="sm" onClick={() => handleItemClick(item)}>
                                View Details
                              </Button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default AdminStatistics
